import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import TrilateralMap from "@/components/TrilateralMap";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Clock, ShieldCheck, ShieldAlert, XCircle, Package, DollarSign, MapPin,
  Navigation, Store as StoreIcon, Home, CheckCircle2, Wallet, Bike, RefreshCw, MessageCircle,
} from "lucide-react";

export default function DriverDashboard() {
  const nav = useNavigate();
  const [rep, setRep] = useState(null);
  const [ofertas, setOfertas] = useState([]);
  const [itinerarios, setItinerarios] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);

  const loadMe = async () => {
    try {
      const { data } = await api.get("/repartidores/me");
      setRep(data.repartidor);
      return data.repartidor;
    } catch { return null; }
  };

  const loadActive = async () => {
    try {
      const [of, it, w] = await Promise.all([
        api.get("/repartidores/ofertas"),
        api.get("/repartidores/itinerario"),
        api.get("/repartidores/wallet"),
      ]);
      setOfertas(of.data.ofertas);
      setItinerarios(it.data.itinerarios);
      setWallet(w.data);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    (async () => {
      const r = await loadMe();
      setLoading(false);
      if (!r) return;
      if (r.status === "incomplete_profile") {
        nav("/repartidor/onboarding");
        return;
      }
      if (r.status === "active") {
        await loadActive();
        pollRef.current = setInterval(loadActive, 12000);
      }
    })();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line
  }, []);

  if (loading || !rep) return (
    <div className="min-h-screen bg-[#F9F9F8]"><Navbar /><div className="p-16 text-center text-zinc-500">Cargando…</div></div>
  );

  if (rep.status === "pending_verification") return <PendingScreen rep={rep} />;
  if (rep.status === "rejected") return <RejectedScreen rep={rep} onRetry={async () => { nav("/repartidor/onboarding"); }} />;

  // status === "active"
  const enRuta = itinerarios.length > 0;

  return (
    <div className="min-h-screen bg-[#F9F9F8]">
      <Navbar />
      <div className="max-w-[1200px] mx-auto px-4 lg:px-8 py-6 space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-br from-zinc-900 to-zinc-800 text-white rounded-sm p-6 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <img
              src={rep.foto_selfie_url || "https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=200"}
              alt="selfie"
              className="w-16 h-16 rounded-full object-cover border-2 border-[#D4FF00]"
            />
            <div>
              <div className="overline text-white/50">Delivery Hero</div>
              <div className="font-display font-black text-2xl lg:text-3xl tracking-tighter">{rep.nombre}</div>
              <div className="text-xs text-white/60 mt-0.5 flex items-center gap-2">
                <Bike className="w-3 h-3" /> {rep.tipo_vehiculo || rep.vehiculo}
                <Badge className="bg-emerald-500 text-white border-0 ml-2" data-testid="badge-active">ACTIVO</Badge>
              </div>
            </div>
          </div>
          {wallet && (
            <div data-testid="wallet-summary" className="bg-white/10 backdrop-blur rounded-sm p-4 min-w-[200px]">
              <div className="overline text-white/50 flex items-center gap-1"><Wallet className="w-3 h-3" /> Mi cartera</div>
              <div className="font-display font-black text-3xl tracking-tighter text-[#D4FF00] mt-1">${wallet.balance.toFixed(2)}</div>
              <div className="text-xs text-white/60 mt-1">Depósito a: {wallet.banco || "—"} •••{wallet.clabe?.slice(-4) || "----"}</div>
            </div>
          )}
        </div>

        {enRuta ? (
          <ActiveDeliveryView key={itinerarios[0].orden_id} itinerario={itinerarios[0]} onDone={loadActive} />
        ) : (
          <OfertasFeed ofertas={ofertas} onAccept={loadActive} />
        )}
      </div>
    </div>
  );
}

function PendingScreen({ rep }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F9F9F8] to-white">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 lg:px-8 py-16 text-center">
        <div className="w-24 h-24 rounded-full bg-amber-100 grid place-items-center mx-auto animate-pulse">
          <Clock className="w-12 h-12 text-amber-600" />
        </div>
        <h1 className="mt-6 font-display font-black text-3xl lg:text-4xl tracking-tighter">
          Estamos revisando tus documentos
        </h1>
        <p className="mt-3 text-zinc-600 leading-relaxed max-w-md mx-auto">
          Gracias por unirte, <b>{rep.nombre}</b>. Nuestro equipo está verificando tu información. Te notificaremos por correo y aquí mismo cuando tu cuenta sea activada.
        </p>
        <div className="mt-8 bg-white border border-zinc-200 rounded-sm p-6 text-left max-w-md mx-auto space-y-3">
          <div className="flex items-center gap-3 text-sm">
            <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
            <span>Datos personales recibidos</span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
            <span>CLABE bancaria registrada ({rep.banco} •••{rep.clabe?.slice(-4)})</span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
            <span>4 documentos cargados</span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <Clock className="w-5 h-5 text-amber-600 shrink-0" />
            <span>Verificación en curso (24-48h)</span>
          </div>
        </div>
        <p className="mt-6 text-xs text-zinc-500">
          Tiempo estimado: 24 a 48 horas hábiles.
        </p>
      </div>
    </div>
  );
}

function RejectedScreen({ rep, onRetry }) {
  return (
    <div className="min-h-screen bg-[#F9F9F8]">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 lg:px-8 py-16 text-center">
        <div className="w-24 h-24 rounded-full bg-red-100 grid place-items-center mx-auto">
          <ShieldAlert className="w-12 h-12 text-red-600" />
        </div>
        <h1 className="mt-6 font-display font-black text-3xl lg:text-4xl tracking-tighter">
          Tu solicitud fue rechazada
        </h1>
        <div className="mt-6 bg-red-50 border border-red-200 rounded-sm p-6 text-left max-w-md mx-auto">
          <div className="overline text-red-700 mb-2">Motivo</div>
          <div data-testid="reject-motivo" className="font-medium text-red-900">{rep.motivo_rechazo || "No especificado"}</div>
        </div>
        <p className="mt-6 text-sm text-zinc-600 max-w-md mx-auto">
          Puedes corregir el problema y volver a subir tus documentos. Nuestro equipo revisará de nuevo tu solicitud.
        </p>
        <Button
          data-testid="btn-resubir"
          onClick={onRetry}
          className="mt-6 h-12 px-8 rounded-sm bg-[#FF3366] hover:bg-[#E62958]"
        >
          <RefreshCw className="w-4 h-4 mr-2" /> Volver a subir documentos
        </Button>
      </div>
    </div>
  );
}

function OfertasFeed({ ofertas, onAccept }) {
  return (
    <div>
      <div className="flex items-end justify-between mb-4">
        <div>
          <div className="overline text-zinc-500">Feed de entregas</div>
          <h2 className="font-display font-black text-2xl tracking-tighter">
            {ofertas.length > 0 ? `${ofertas.length} entrega${ofertas.length > 1 ? "s" : ""} disponible${ofertas.length > 1 ? "s" : ""}` : "Sin entregas disponibles"}
          </h2>
        </div>
      </div>

      {ofertas.length === 0 ? (
        <div className="bg-white border border-dashed border-zinc-300 rounded-sm p-14 text-center text-zinc-500 text-sm">
          🏃 Espera un momento… Cuando una tienda marque un pedido como "Listo para recoger", aparecerá aquí.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {ofertas.map(o => <OfertaCard key={o.orden_id} oferta={o} onAccept={onAccept} />)}
        </div>
      )}
    </div>
  );
}

function OfertaCard({ oferta, onAccept }) {
  const [busy, setBusy] = useState(false);
  const aceptar = async () => {
    setBusy(true);
    try {
      await api.post("/repartidores/aceptar", { orden_id: oferta.orden_id });
      toast.success("¡Entrega aceptada! Vamos por ella.");
      onAccept();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const t = oferta.tiendas[0] || {};
  return (
    <div data-testid={`oferta-${oferta.orden_id}`} className="bg-white border border-zinc-200 rounded-sm overflow-hidden hover:border-[#FF3366] transition-colors">
      <div className="p-4 border-b border-zinc-100 flex items-center justify-between">
        <div>
          <div className="overline text-zinc-500">Orden {oferta.orden_token}</div>
          <div className="text-xs text-zinc-500 mt-0.5">{oferta.tiendas.length} tienda{oferta.tiendas.length > 1 ? "s" : ""}</div>
        </div>
        <div className="text-right">
          <div className="overline text-emerald-600 flex items-center gap-1 justify-end"><DollarSign className="w-3 h-3" /> Ganas</div>
          <div className="font-display font-black text-2xl tracking-tighter text-emerald-600">${oferta.ganancia.toFixed(2)}</div>
        </div>
      </div>
      <div className="p-4 space-y-3">
        <div className="flex items-start gap-2">
          <StoreIcon className="w-4 h-4 text-[#FF3366] mt-0.5 shrink-0" />
          <div className="text-sm">
            <div className="font-medium">Recoger en</div>
            <div className="text-zinc-600 text-xs">{t.nombre_local || "—"}</div>
            <div className="text-zinc-500 text-xs">{t.direccion || t.ciudad || ""}</div>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <Home className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
          <div className="text-sm">
            <div className="font-medium">Entregar a {oferta.cliente_nombre || "cliente"}</div>
            <div className="text-zinc-500 text-xs">{oferta.cliente_direccion || "Sin dirección"}</div>
          </div>
        </div>
        <div className="pt-2 border-t border-zinc-100 flex items-center justify-between text-sm">
          <span className="text-zinc-500 text-xs">Cobrar (COD)</span>
          <span className="font-display font-bold text-lg">${oferta.total_a_cobrar.toFixed(2)}</span>
        </div>
        <Button
          data-testid={`aceptar-${oferta.orden_id}`}
          onClick={aceptar}
          disabled={busy}
          className="w-full h-12 rounded-sm bg-[#FF3366] hover:bg-[#E62958] font-display font-bold tracking-tight"
        >
          {busy ? "Aceptando…" : "Aceptar Entrega"}
        </Button>
      </div>
    </div>
  );
}

function ActiveDeliveryView({ itinerario, onDone }) {
  const [busy, setBusy] = useState(false);
  const [showCollect, setShowCollect] = useState(false);
  const [gps, setGps] = useState(null); // {lat,lng,at}
  const [gpsError, setGpsError] = useState(null);
  const [tracking, setTracking] = useState(null); // {driver, stores, buyer}
  const watchIdRef = useRef(null);
  const lastPushRef = useRef(0);
  const orden_id = itinerario.orden_id;
  const recolecciones = itinerario.paradas.filter(p => p.tipo === "recoger");
  const entrega = itinerario.paradas.find(p => p.tipo === "entregar");
  const total = itinerario.total;

  // Fase 8: GPS background tracking (watchPosition + PATCH cada 15s al backend)
  useEffect(() => {
    if (!navigator.geolocation) {
      setGpsError("Tu navegador no soporta GPS");
      return;
    }
    watchIdRef.current = navigator.geolocation.watchPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        setGps({ lat, lng, at: Date.now() });
        if (Date.now() - lastPushRef.current >= 15000) {
          lastPushRef.current = Date.now();
          try { await api.patch("/repartidores/location", { lat, lng }); }
          catch (e) { console.error("GPS push failed:", e); }
        }
      },
      (err) => setGpsError(err.message),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 }
    );
    return () => {
      if (watchIdRef.current !== null) navigator.geolocation.clearWatch(watchIdRef.current);
    };
  }, []);

  // Fase 9: fetch tracking points (driver, stores, buyer) + refresh cada 20s
  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get(`/ordenes/${orden_id}/tracking-points`);
        if (gps) data.driver = { ...(data.driver || {}), lat: gps.lat, lng: gps.lng };
        setTracking(data);
      } catch (e) { console.error("tracking-points fetch failed:", e); }
    };
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [orden_id, gps?.lat, gps?.lng]);

  const navUrl = entrega ? `https://www.google.com/maps/dir/?api=1&destination=${entrega.lat},${entrega.lng}` : "";

  const waMsg = (msg) => `https://wa.me/${(entrega?.telefono || "").replace(/\D/g, "")}?text=${encodeURIComponent(msg)}`;
  const waAviso = waMsg("¡Hola! Soy el repartidor de en 90 MINUTOS. Ya me encuentro afuera de la dirección para entregarte tu pedido de ropa/calzado. Quedo atento para entregártelo.");

  const llegueTienda = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/repartidores/llegue-tienda/${orden_id}`);
      toast.success(`Notificaste a ${data.notificadas} tienda(s)`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const entregaExitosa = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/repartidores/entrega-exitosa/${orden_id}`);
      toast.success(`¡Ganaste $${data.ganancia_acreditada.toFixed(2)}! Ya en tu cartera.`);
      setShowCollect(false);
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="active-delivery" className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
      {/* Fase 8: GPS Activo badge pulsante */}
      <div className={`px-4 py-2 flex items-center gap-2 text-xs ${gpsError ? "bg-red-50 text-red-700 border-b border-red-200" : "bg-emerald-50 text-emerald-700 border-b border-emerald-200"}`}>
        {gpsError ? (
          <>
            <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
            <span data-testid="gps-status" className="font-medium">GPS inactivo: {gpsError}</span>
          </>
        ) : (
          <>
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
            </span>
            <span data-testid="gps-status" className="font-medium">GPS Activo y Monitoreado</span>
            {gps && <span className="text-emerald-600/70 ml-auto font-mono">{gps.lat.toFixed(4)}, {gps.lng.toFixed(4)}</span>}
          </>
        )}
      </div>

      <div className="p-4 border-b border-zinc-100 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline text-zinc-500">Entrega en curso</div>
          <div className="font-display font-black text-xl tracking-tighter">Orden {itinerario.orden_token}</div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-blue-100 text-blue-700 border-0">DISPATCHED</Badge>
        </div>
      </div>

      {/* Mapa trilateral Leaflet (Fase 9) */}
      <div className="border-b border-zinc-100 relative">
        <TrilateralMap
          driver={tracking?.driver || (gps ? { lat: gps.lat, lng: gps.lng, nombre: "Tú", vehiculo: "Moto" } : null)}
          stores={tracking?.stores || []}
          buyer={tracking?.buyer}
          height="h-72"
        />
        {navUrl && (
          <a href={navUrl} target="_blank" rel="noreferrer" className="absolute bottom-3 right-3 z-[1000]">
            <Button size="sm" className="rounded-sm bg-white text-zinc-900 hover:bg-zinc-100 shadow-lg">
              <Navigation className="w-4 h-4 mr-1" /> Abrir en Google Maps
            </Button>
          </a>
        )}
      </div>

      {/* Paradas + acciones */}
      <div className="p-5 space-y-4">
        <div>
          <div className="overline text-zinc-500 mb-2">Recoger en</div>
          {recolecciones.map((p, i) => (
            <div key={p.tienda_id || `pickup-${i}`} className="border border-zinc-200 rounded-sm p-3 mb-2 flex items-start gap-3">
              <StoreIcon className="w-5 h-5 text-[#FF3366] mt-0.5 shrink-0" />
              <div className="flex-1">
                <div className="font-medium">{p.nombre_local}</div>
                <div className="text-xs text-zinc-500">{p.items_count} artículos · Token: <span className="font-mono">{p.token_verificacion}</span></div>
              </div>
            </div>
          ))}
        </div>

        {entrega && (
          <div>
            <div className="overline text-zinc-500 mb-2">Entregar a</div>
            <div className="border border-zinc-200 rounded-sm p-3 flex items-start gap-3">
              <Home className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
              <div className="flex-1">
                <div className="font-medium">{entrega.cliente_nombre}</div>
                <div className="text-xs text-zinc-500">{entrega.direccion || "Sin dirección"}</div>
                {entrega.telefono && <div className="text-xs text-[#FF3366] mt-1">{entrega.telefono}</div>}
              </div>
            </div>
          </div>
        )}

        {/* Botones de acción */}
        <div className="grid md:grid-cols-3 gap-3 pt-3 border-t border-zinc-100">
          <Button
            data-testid="btn-llegue-tienda"
            onClick={llegueTienda}
            disabled={busy}
            variant="outline"
            className="h-14 rounded-sm border-2 border-zinc-300 hover:border-[#FF3366]"
          >
            <StoreIcon className="w-5 h-5 mr-2" /> Llegué a la Tienda
          </Button>
          <a href={entrega?.telefono ? waAviso : "#"} target="_blank" rel="noreferrer" onClick={e => { if (!entrega?.telefono) { e.preventDefault(); toast.error("Cliente sin WhatsApp"); } }}>
            <Button
              data-testid="btn-wa-avisar-llegue"
              variant="outline"
              className="h-14 w-full rounded-sm border-2 border-emerald-400 text-emerald-700 hover:bg-emerald-50"
            >
              <MessageCircle className="w-5 h-5 mr-2" /> Avisar que llegué
            </Button>
          </a>
          <Button
            data-testid="btn-entrega-exitosa"
            onClick={() => setShowCollect(true)}
            disabled={busy}
            className="h-14 rounded-sm bg-emerald-500 hover:bg-emerald-600 text-white font-display font-bold tracking-tight"
          >
            <CheckCircle2 className="w-5 h-5 mr-2" /> Entrega Exitosa y Cobrado
          </Button>
        </div>
      </div>

      <Dialog open={showCollect} onOpenChange={setShowCollect}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader>
            <DialogTitle>Confirmar cobro y entrega</DialogTitle>
          </DialogHeader>
          <div className="text-center py-4">
            <DollarSign className="w-16 h-16 text-emerald-500 mx-auto" />
            <div className="mt-3 text-sm text-zinc-600">Monto cobrado al cliente (COD)</div>
            <div className="font-display font-black text-5xl tracking-tighter mt-2">${total.toFixed(2)}</div>
            <div className="mt-4 text-xs text-zinc-500">
              Al confirmar, se acreditará tu ganancia a tu cartera y la orden pasará a Entregado.
            </div>
          </div>
          <Button
            data-testid="btn-confirmar-cobro"
            onClick={entregaExitosa}
            disabled={busy}
            className="h-12 rounded-sm bg-emerald-500 hover:bg-emerald-600 text-white font-bold"
          >
            {busy ? "Procesando…" : "Confirmar entrega y cobro"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
